import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { forwardRef, useCallback, useRef } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

import { cn } from '../../utils/cn';
import { Button } from '../button';
import { Input } from '../input';
import { LoadingAddon } from '../loading-addon';

export interface InputNumberProps extends Omit<NumericFormatProps, 'value' | 'onValueChange'> {
  decimalScale?: number;
  defaultValue?: number;
  fixedDecimalScale?: boolean;
  isLoading?: boolean;
  max?: number;
  min?: number;
  placeholder?: string;
  prefix?: string;
  stepper?: number;
  suffix?: string;
  thousandSeparator?: string;
  value?: number | string;
  onValueChange?: (value: number | undefined) => void;
}

export const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      className,
      decimalScale = 0,
      disabled,
      fixedDecimalScale = false,
      isLoading,
      max = Infinity,
      min = -Infinity,
      placeholder,
      prefix,
      stepper = 1,
      suffix,
      thousandSeparator = ',',
      value,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = (ref as React.RefObject<HTMLInputElement | null>) ?? inputRef;

    const numericValue = typeof value === 'number' ? value : undefined;

    const clamp = useCallback((val: number) => Math.min(Math.max(val, min), max), [min, max]);

    const changeValue = useCallback(
      (delta: number) => {
        const base = typeof numericValue === 'number' ? numericValue : 0;
        const newValue = clamp(base + delta);
        onValueChange?.(newValue);
      },
      [numericValue, onValueChange, clamp],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        changeValue(stepper);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        changeValue(-stepper);
      }
    };

    const handleChange = (values: { value: string; floatValue: number | undefined }) => {
      onValueChange?.(values.floatValue);
    };

    const handleBlur = () => {
      if (numericValue === undefined) return;
      const clamped = clamp(numericValue);
      if (clamped !== numericValue) {
        onValueChange?.(clamped);
      }
    };

    return (
      <div className="relative flex items-center">
        <NumericFormat
          autoComplete="off"
          value={numericValue ?? ''}
          onValueChange={handleChange}
          thousandSeparator={thousandSeparator}
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          allowNegative={min < 0}
          valueIsNumericString
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          max={max}
          min={min}
          suffix={suffix}
          prefix={prefix}
          customInput={Input}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            'relative [appearance:textfield] rounded-r-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            className,
          )}
          getInputRef={combinedRef}
          {...props}
        />

        <div className="flex flex-col">
          <Button
            aria-label="Increase value"
            className="h-[18px] rounded-l-none rounded-br-none border-b-0 border-l-0 border-input px-3 focus-visible:relative"
            variant="outline"
            onClick={() => changeValue(stepper)}
            disabled={disabled || (numericValue !== undefined && numericValue >= max)}
          >
            <ChevronUpIcon />
          </Button>
          <Button
            aria-label="Decrease value"
            className="h-[18px] rounded-l-none rounded-tr-none border-t-0 border-l-0 border-input px-3 focus-visible:relative"
            variant="outline"
            onClick={() => changeValue(-stepper)}
            disabled={disabled || (numericValue !== undefined && numericValue <= min)}
          >
            <ChevronDownIcon />
          </Button>
        </div>

        {isLoading && <LoadingAddon type="plain" />}
      </div>
    );
  },
);
